"use client";

import React from "react";
import { Building2, Factory, Wrench, HardHat, ShieldCheck, CircleCheck as CheckCircle2, ArrowRight, Layers, Clock } from 'lucide-react';
import { motion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
const audiences = [{
  title: "Industrial operators",
  desc: "Planned shutdowns, mechanical overhauls, and continuous facility upgrades."
}, {
  title: "Facilities managers",
  desc: "Routine roof maintenance, lighting refits, HVAC access, and structural repairs."
}, {
  title: "Main contractors",
  desc: "Commercial fit-outs, warehouse extensions, and complex industrial installations."
}];
const useCases = [{
  icon: Building2,
  title: "Warehouse maintenance access",
  description: "High-bay lighting access, racking inspections, gutter clears, and roof repairs arranged to keep daily logistics moving without downtime."
}, {
  icon: Factory,
  title: "Plant & machinery access",
  description: "Tailored cantilever scaffolds, bridging systems, and perimeter decks engineered around sensitive processing lines and utilities."
}, {
  icon: Layers,
  title: "Internal birdcages & crash decks",
  description: "Full-coverage working platforms and heavy-duty crash decks providing safe overhead protection during ceiling and cladding renewals."
}];
const capabilities = ["Bespoke structural scaffolding design and calculations", "NASC and CHAS accredited safety systems", "Out-of-hours and phased erection around 24/7 site operations", "Full handover certification and regular statutory 7-day inspections"];
export default function IndustrialScaffolding() {
  return <section id="industrialscaffoldingoverview" data-nav="light" className="relative bg-background py-16 md:py-24 text-foreground overflow-hidden">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header Block */}
        <div className="max-w-4xl">
          <div className="flex flex-wrap items-center gap-2 mb-4">
            <Badge variant="outline" className="text-foreground border-border bg-card">
              Industrial & Commercial Access
            </Badge>
            <span className="text-xs text-muted-foreground">South East & Home Counties</span>
          </div>

          <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight text-foreground">
            Industrial scaffolding
          </h1>

          <p className="mt-4 text-lg md:text-xl text-muted-foreground leading-relaxed">
            Engineered access systems built for active industrial plants, commercial warehouses, and distribution facilities across Berkshire, Surrey, Hampshire, Buckinghamshire, London, and the South East.
          </p>

          <div className="mt-6 flex flex-wrap items-center gap-3">
            <Button asChild size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90">
              <Link to="/ContactRequestQuotePage">
                Request a site assessment
                <ArrowRight className="size-4" />
              </Link>
            </Button>
            <Button asChild variant="outline" size="lg" className="border-border hover:bg-muted">
              <Link to="/HealthSafetyAccreditationsPage">
                View safety accreditations
              </Link>
            </Button>
          </div>
        </div>

        {/* Audience Strip */}
        <div className="mt-12 pt-8 border-t border-border">
          <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-4">
            Access solutions for key site stakeholders
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {audiences.map((aud, i) => <div key={aud.title} data-index={i} className="rounded-lg border border-border bg-card p-4 transition-colors hover:border-primary/40">
                <div className="font-semibold text-foreground text-sm flex items-center gap-2">
                  <span className="size-2 rounded-full bg-primary" />
                  {aud.title}
                </div>
                <p className="mt-1 text-xs text-muted-foreground leading-normal">
                  {aud.desc}
                </p>
              </div>)}
          </div>
        </div>

        {/* Primary Use Cases Grid */}
        <div className="mt-14">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-8">
            <div>
              <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-foreground">
                Core industrial applications
              </h2>
              <p className="mt-1 text-sm md:text-base text-muted-foreground">
                Engineered solutions that maintain operational workflows while guaranteeing compliant safety boundaries.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {useCases.map((uc, i) => {
            const Icon = uc.icon;
            return <Card key={uc.title} data-index={i} className="border-border bg-card shadow-sm hover:shadow-md transition-[box-shadow,border-color] duration-300 hover:border-primary/40 flex flex-col justify-between">
                  <CardHeader>
                    <div className="size-11 rounded-md bg-accent flex items-center justify-center text-primary mb-3">
                      <Icon className="size-5" />
                    </div>
                    <CardTitle className="text-xl font-bold text-foreground">
                      {uc.title}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <CardDescription className="text-sm text-muted-foreground leading-relaxed">
                      {uc.description}
                    </CardDescription>
                  </CardContent>
                </Card>;
          })}
          </div>
        </div>

        {/* Operational & Safety Assurance Banner */}
        <div className="mt-12 rounded-xl border border-border bg-card p-6 sm:p-8 lg:p-10 shadow-sm">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
            <div className="lg:col-span-7">
              <div className="flex items-center gap-2 text-primary text-xs font-semibold uppercase tracking-wider mb-2">
                <HardHat className="size-4" />
                Operational Delivery & Risk Management
              </div>
              <h3 className="text-xl sm:text-2xl font-bold text-foreground">
                Coordinated around live industrial operations
              </h3>
              <p className="mt-3 text-sm sm:text-base text-muted-foreground leading-relaxed">
                Industrial sites cannot afford unmanaged downtime or loose safety protocols. Guided by over 20 years of hands-on sector leadership from Pete Wood and our qualified scaffolding team, BMF provides site-specific method statements, risk assessments, and planned access schedules that fit cleanly around live manufacturing lines and distribution traffic.
              </p>

              <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3">
                {capabilities.map((cap, i) => <div key={cap} data-index={i} className="flex items-start gap-2.5 text-xs sm:text-sm text-foreground">
                    <CheckCircle2 className="size-4 text-primary shrink-0 mt-0.5" />
                    <span>{cap}</span>
                  </div>)}
              </div>
            </div>

            <div className="lg:col-span-5 flex flex-col gap-4 bg-muted/40 p-5 sm:p-6 rounded-lg border border-border/80">
              <div className="flex items-center gap-3">
                <div className="size-10 rounded-full bg-primary/10 text-primary flex items-center justify-center shrink-0">
                  <ShieldCheck className="size-5" />
                </div>
                <div>
                  <h4 className="font-semibold text-foreground text-sm">Verified Accreditations</h4>
                  <p className="text-xs text-muted-foreground">Certified compliance for high-risk environments</p>
                </div>
              </div>

              <Separator />

              <div className="grid grid-cols-3 gap-2 text-center">
                <div className="rounded border border-border bg-card p-2">
                  <span className="block font-bold text-sm text-foreground">NASC</span>
                  <span className="text-[10px] text-muted-foreground">Full Member</span>
                </div>
                <div className="rounded border border-border bg-card p-2">
                  <span className="block font-bold text-sm text-foreground">CHAS</span>
                  <span className="text-[10px] text-muted-foreground">Accredited</span>
                </div>
                <div className="rounded border border-border bg-card p-2">
                  <span className="block font-bold text-sm text-foreground">CLINE</span>
                  <span className="text-[10px] text-muted-foreground">Gold Verified</span>
                </div>
              </div>

              <p className="text-xs text-muted-foreground leading-normal mt-1">
                Every structure is erected by CISRS-qualified scaffolders adhering strictly to TG20:21 and SG4:22 industry safety standards.
              </p>
            </div>
          </div>
        </div>

      </div>
    </section>;
}